import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Grid, Card, CardHeader, CardContent, Chip, IconButton, Tooltip, Typography, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';
import { BasicFlex, Notes } from '../../../utils/Common';

import { usePageData } from '../../../contexts/PageData';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency, formatPhoneNumber } from '../../../utils/util';
import { useState } from 'react';

import { startCase } from 'lodash';

import VisaIcon from '../../../images/cc/Visa';
import MastercardIcon from '../../../images/cc/Mastercard';
import AmexIcon from '../../../images/cc/Amex';

// ─── helpers ────────────────────────────────────────────────────────────────

function isExpiringSoon(mm, yy) {
  const now = new Date();
  const expiry = new Date(2000 + Number(yy), Number(mm) - 1, 1);
  const diff = (expiry - now) / (1000 * 60 * 60 * 24 * 30);
  return diff <= 3 && diff >= 0;
}

function isExpired(mm, yy) {
  const now = new Date();
  const expiry = new Date(2000 + Number(yy), Number(mm) - 1, 1);
  return expiry < now;
}

// ─── brand logos ─────────────────────────────────────────────────────────────

const BRAND_LOGO = {
  mastercard: <MastercardIcon />,
  visa: <VisaIcon />,
  amex: <AmexIcon />,
};

// ─── animations ──────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.55; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── styled ───────────────────────────────────────────────────────────────────

const CardTrack = styled(BasicFlex)`
  gap: 1.4rem;
  justify-content: flex-start;
  padding-bottom: 12px;
  padding-top: 4px;
  flex-wrap: wrap;
`;

const CardTile = styled(Box, {
  shouldForwardProp: (p) => !['selected', 'warn', 'expired'].includes(p),
})`
  flex: 0 0 250px;
  min-height: 150px;
  border-radius: 16px;
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  scroll-snap-align: start;
  cursor: pointer;
  position: relative;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  animation: ${slideIn} 0.32s ease both;
  background-color: ${({ theme, selected }) =>
    selected
      ? theme.palette.mode === 'dark'
        ? 'rgba(25, 118, 210, 0.08)'
        : '#f0f6ff'
      : theme.palette.background.paper};
  border: ${({ theme, selected, warn, expired }) =>
    selected
      ? `2px solid ${theme.palette.secondary.main}`
      : expired
      ? `1.5px solid ${theme.palette.error.light}`
      : warn
      ? `1.5px solid ${theme.palette.warning.main}`
      : `1px solid ${theme.palette.divider}`};
  box-shadow: ${({ theme, selected }) =>
    selected ? `0 0 0 4px ${theme.palette.secondary.main}22` : 'none'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme, selected }) =>
      selected
        ? `0 4px 20px ${theme.palette.secondary.main}33`
        : '0 4px 16px rgba(0,0,0,0.08)'};
    .remove-btn { opacity: 1; }
  }
`;

const RemoveBtn = styled(IconButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  opacity: 0;
  transition: opacity 0.15s;
  padding: 3px;
  &:hover { color: ${({ theme }) => theme.palette.error.main}; }
`;

const AddTile = styled(Box)`
  flex: 0 0 230px;
  min-height: 150px;
  border-radius: 16px;
  border: 1.5px dashed ${({ theme }) => theme.palette.divider};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};
  scroll-snap-align: start;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.palette.secondary.main};
    color: ${({ theme }) => theme.palette.secondary.main};
    background: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(25,118,210,0.06)' : '#f0f6ff'};
  }
`;

const ExpiryWarning = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 3px 8px;
  border-radius: 8px;
  width: fit-content;
  animation: ${pulse} 2.4s ease infinite;
  background-color: ${({ theme, expired }) =>
    expired ? `${theme.palette.error.light}28` : `${theme.palette.warning.light}38`};
  color: ${({ theme, expired }) =>
    expired ? theme.palette.error.main : theme.palette.warning.dark};
`;

const SetDefaultBtn = styled('button')`
  background: none;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 8px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-family: inherit;
  transition: border-color 0.15s, color 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const DotsRow = styled(Box)`
  display: flex;
  gap: 5px;
  justify-content: center;
  margin-top: 10px;
`;

const Dot = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})`
  height: 4px;
  width: ${({ active }) => (active ? '20px' : '6px')};
  border-radius: 99px;
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease;
  background: ${({ theme, active }) =>
    active ? theme.palette.primary.main : theme.palette.divider};
`;

// ─── sample data (replace with API response) ─────────────────────────────────

const SAMPLE_CARDS = [
  { id: '1', brand: 'mastercard', last4: '4242', expiry: '08 / 27', isDefault: true },
  { id: '2', brand: 'visa',       last4: '9901', expiry: '05 / 26', isDefault: false },
  { id: '3', brand: 'amex',       last4: '0012', expiry: '05 / 25', isDefault: false },
];

// ─── SavedCards ───────────────────────────────────────────────────────────────

const SavedCards = ({ cards: initialCards = SAMPLE_CARDS, onAdd, onRemove, onSetDefault }) => {
  const [cards, setCards] = useState(initialCards);
  const [selectedId, setSelectedId] = useState(
    initialCards.find((c) => c.isDefault)?.id ?? initialCards[0]?.id
  );

  const handleRemove = (e, id) => {
    e.stopPropagation();
    setCards((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (selectedId === id && next[0]) setSelectedId(next[0].id);
      return next;
    });
    onRemove?.(id);
  };

  const handleSetDefault = (e, id) => {
    e.stopPropagation();
    setCards((prev) => prev.map((c) => ({ ...c, isDefault: c.id === id })));
    onSetDefault?.(id);
  };

  return (
    <Box>
      <CardTrack>
        {cards.map((card, i) => {
          const [mm, yy] = card.expiry.split('/').map((s) => s.trim());
          const warn = isExpiringSoon(mm, yy);
          const expired = isExpired(mm, yy);
          const selected = card.id === selectedId;

          return (
            <CardTile
              key={card.id}
              selected={selected}
              warn={warn ? 1 : 0}
              expired={expired ? 1 : 0}
              onClick={() => setSelectedId(card.id)}
              sx={{ animationDelay: `${i * 0.06}s` }}
            >
              <RemoveBtn
                className="remove-btn"
                size="small"
                onClick={(e) => handleRemove(e, card.id)}
                aria-label="Remove card"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </RemoveBtn>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {BRAND_LOGO[card.brand] ?? null}
                {card.isDefault && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                    label="Default"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11, fontWeight: 600, '& .MuiChip-label': { px: '6px' }, position: 'relative', right: ".2rem", top: ".5rem" }}
                  />
                )}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, letterSpacing: '0.05em', mb: 0.25 }}>
                  •••• •••• •••• {card.last4}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  {warn || expired ? (
                    <ExpiryWarning expired={expired ? 1 : 0}>
                      <WarningAmberIcon sx={{ fontSize: 12 }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1 }}>
                        {expired ? 'Expired' : `Expires ${card.expiry}`}
                      </Typography>
                    </ExpiryWarning>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {startCase(card.brand)} · {card.expiry}
                    </Typography>
                  )}

                  {!card.isDefault && (
                    <Tooltip title="Use as default payment method" placement="top" arrow>
                      <SetDefaultBtn onClick={(e) => handleSetDefault(e, card.id)}>
                        Set default
                      </SetDefaultBtn>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </CardTile>
          );
        })}

        <AddTile onClick={onAdd} role="button" aria-label="Add new card">
          <AddIcon sx={{ fontSize: 26 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Add new card</Typography>
        </AddTile>
      </CardTrack>

      {/* <DotsRow>
        {cards.map((c, i) => (
          <Dot key={c.id} active={i === selectedIdx} onClick={() => setSelectedId(c.id)} />
        ))}
        <Dot active={false} />
      </DotsRow> */}
    </Box>
  );
};

// ─── CardTab ──────────────────────────────────────────────────────────────────

const CardTab = () => {
  const { data = {} } = usePageData();

  // const { data: cards } = useQuery(['cards', data.customer_id], () => getCards(data.customer_id));

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Card elevation={0}>
          <CardHeader title="Saved Cards" />
          <CardContent>
            <SavedCards
              // cards={cards}                           // ← swap in when API is wired
              onAdd={() => console.log('open add card modal')}
              onRemove={(id) => console.log('remove', id)}
              onSetDefault={(id) => console.log('set default', id)}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CardTab;